import { query } from "../../../erd-editor-schema/dist/query/index.js";
import { arrayHas } from "../../../utils/arrayHas.js";
import { textInRange } from "../../../utils/validation.js";
import { createAction } from "../../../r-html/dist/store/index.js";
import { ActionType, DictionaryKind, DictionaryKindList, DictionaryStoredFields } from "./actions.js";
import { domainColumnIds } from "./domainLinks.js";
import { getLiveDomainName, listId, unlistId } from "./membership.js";
import { createDomain } from "../../../utils/collection/domain.entity.js";
import { createTerm } from "../../../utils/collection/term.entity.js";
import { createWord } from "../../../utils/collection/word.entity.js";
import { raiseDictionaryGeneration } from "./dictionaryIndex.js";
import { isString } from "es-toolkit";
//#region src/engine/modules/dictionary/atom.actions.ts
var COLLECTION_KEY = {
	[DictionaryKind.word]: "wordEntities",
	[DictionaryKind.term]: "termEntities",
	[DictionaryKind.domain]: "domainEntities"
};
var IDS_KEY = {
	[DictionaryKind.word]: "wordIds",
	[DictionaryKind.term]: "termIds",
	[DictionaryKind.domain]: "domainIds"
};
var CREATE = {
	[DictionaryKind.word]: createWord,
	[DictionaryKind.term]: createTerm,
	[DictionaryKind.domain]: createDomain
};
var hasDictionaryKind = arrayHas(DictionaryKindList);
var dictionaryCollection = (state, kind) => query(state.collections).collection(COLLECTION_KEY[kind]);
var selectDictionaryRow = (state, kind, id) => dictionaryCollection(state, kind).selectById(id);
var dictionaryIds = (state, kind) => state.doc[IDS_KEY[kind]];
function dictionaryEntries(kind, values) {
	if (!values || typeof values !== "object") return [];
	const entries = [];
	for (const field of DictionaryStoredFields[kind]) {
		const value = Reflect.get(values, field);
		if (isString(value)) entries.push([field, value]);
	}
	return entries;
}
function writeDictionaryValues(state, kind, id, values, version) {
	const collection = dictionaryCollection(state, kind);
	let renamed = false;
	for (const [field, value] of dictionaryEntries(kind, values)) collection.replaceOperator(state.lww, version, id, field, () => {
		collection.updateOne(id, (entity) => {
			Reflect.set(entity, field, value);
		});
		if (field === "name") renamed = true;
	});
	return renamed && kind === DictionaryKind.domain;
}
function measureDomainColumns(state, domainId, { toWidth }) {
	const width = textInRange(toWidth(getLiveDomainName(state, domainId)));
	const columns = state.collections.tableColumnEntities;
	if (!domainId) {
		for (const column of Object.values(columns)) if (column.domainId === domainId) column.ui.widthDomain = width;
		return;
	}
	for (const id of domainColumnIds(state, domainId)) columns[id].ui.widthDomain = width;
}
var addDictionaryAction = createAction(ActionType.addDictionary);
var addDictionary = (state, { payload: { kind, id, values }, version }, ctx) => {
	if (!hasDictionaryKind(kind)) return;
	raiseDictionaryGeneration(state, kind);
	const safeVersion = version ?? ctx.clock.getVersion();
	const collection = dictionaryCollection(state, kind);
	collection.getOrCreate(id, (id) => CREATE[kind]({ id }));
	let listed = false;
	collection.addOperator(state.lww, safeVersion, id, () => {
		listed = listId(dictionaryIds(state, kind), id);
	});
	const renamed = writeDictionaryValues(state, kind, id, values, safeVersion);
	if (kind === DictionaryKind.domain && (listed || renamed)) measureDomainColumns(state, id, ctx);
};
var updateDictionaryAction = createAction(ActionType.updateDictionary);
var updateDictionary = (state, { payload: { kind, id, values }, version }, ctx) => {
	if (!hasDictionaryKind(kind)) return;
	raiseDictionaryGeneration(state, kind);
	const safeVersion = version ?? ctx.clock.getVersion();
	dictionaryCollection(state, kind).getOrCreate(id, (id) => CREATE[kind]({ id }));
	if (writeDictionaryValues(state, kind, id, values, safeVersion)) measureDomainColumns(state, id, ctx);
};
var removeDictionaryAction = createAction(ActionType.removeDictionary);
var removeDictionary = (state, { payload: { kind, id }, version }, ctx) => {
	if (!hasDictionaryKind(kind)) return;
	raiseDictionaryGeneration(state, kind);
	const safeVersion = version ?? ctx.clock.getVersion();
	let unlisted = false;
	dictionaryCollection(state, kind).removeOperator(state.lww, safeVersion, id, () => {
		unlisted = unlistId(dictionaryIds(state, kind), id);
	});
	if (kind === DictionaryKind.domain && unlisted) measureDomainColumns(state, id, ctx);
};
var dictionaryReducers = {
	[ActionType.addDictionary]: addDictionary,
	[ActionType.updateDictionary]: updateDictionary,
	[ActionType.removeDictionary]: removeDictionary
};
var actions = {
	addDictionaryAction,
	updateDictionaryAction,
	removeDictionaryAction
};
//#endregion
export { actions, addDictionaryAction, dictionaryEntries, dictionaryIds, dictionaryReducers, hasDictionaryKind, measureDomainColumns, removeDictionaryAction, selectDictionaryRow, updateDictionaryAction };
